import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getPreferencesByTenantId, getPreferencesByTenantIdAndFeature, updateDashboardPreferencesByTenantIdAndFeature, getUserPreferencesByUserId, getUserPreferencesByUserIdAndFeature, updateUserPreferencesByUserIdAndFeature } from "../api/tenantApi";
import { getTenantIdAuth0, isAdminAuth0, getCurrentUserId } from "../authUtils";
import getTenantIdFromURL from "../utils/getTenantIdFromURL";
import { normalizeNotificationPreferences, NOTIFICATION_CHANNEL_FEATURES } from "../utils/notificationPreferenceSettings";

const UserPreferencesContext = createContext(undefined);

export const UserPreferencesProvider = ({ children }) => {
  const [userPreferences, setUserPreferences] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserPreferences = async () => {
      try {
        console.log('🚀 UserPreferences fetching preferences...');
        const isAdmin = await isAdminAuth0();
        const selectedTenant = getTenantIdFromURL();
        const tenantId = isAdmin ? selectedTenant : await getTenantIdAuth0();
        const userId = getCurrentUserId();
        
        console.log("tenantId here in preference", tenantId);
        console.log("userId here in preference", userId);
        
        const preferences = {};
        
        // Fetch tenant-specific preferences (for Slack)
        try {
          const tenantResult = await getPreferencesByTenantId(tenantId);
          console.log("INFO Tenant Preferences result -> ", JSON.stringify(tenantResult, null, 2));
          
          const tenantItems = Array.isArray(tenantResult) ? tenantResult : tenantResult?.data;
          if (Array.isArray(tenantItems)) {
            tenantItems.forEach((item) => {
              // Only include Slack preferences from tenant
              if (item.feature === "Slack Notification Preferences") {
                preferences.slack = normalizeNotificationPreferences(item.preferences);
              }
            });
          }
        } catch (error) {
          console.error("Error fetching tenant preferences:", error);
        }
        
        // Fetch user-specific preferences (for inApp, email, weekly)
        if (userId) {
          try {
            const userResult = await getUserPreferencesByUserId(userId, tenantId);
            console.log("INFO User Preferences result -> ", JSON.stringify(userResult, null, 2));
            
            const userItems = Array.isArray(userResult) ? userResult : userResult?.data;
            if (Array.isArray(userItems)) {
              userItems.forEach((item) => {
                // Map user preferences to the correct channel
                switch (item.feature) {
                  case "Notification Preferences":
                    preferences.inApp = normalizeNotificationPreferences(item.preferences);
                    break;
                  case "Email Notification Preferences":
                    preferences.email = normalizeNotificationPreferences(item.preferences);
                    break;
                  case "Weekly Notification Preferences":
                    preferences.weekly = normalizeNotificationPreferences(item.preferences);
                    break;
                  default:
                    break;
                }
              });
            }
          } catch (error) {
            console.error("Error fetching user preferences:", error);
          }
        }
        
        setUserPreferences(preferences);
        console.log("INFO Combined User Preferences -> ", preferences);
      } catch (error) {
        console.error("Unable to fetch user preferences. Error JSON:", JSON.stringify(error, null, 2));
      } finally {
        setLoading(false);
      }
    };

    fetchUserPreferences();
  }, []);

  const updateUserPreferences = async (tenantId, feature, preferences) => {
    const channel = Object.keys(NOTIFICATION_CHANNEL_FEATURES).find(key => NOTIFICATION_CHANNEL_FEATURES[key] === feature);
    if (!channel) throw new Error('Unknown notification preference feature');
    const values = normalizeNotificationPreferences(preferences?.preferences ?? preferences);
    const data = { preferences: values, tenantId };
    let response;
    if (channel !== 'slack') {
      const userId = getCurrentUserId();
      if (!userId) throw new Error('User ID not found');
      await updateUserPreferencesByUserIdAndFeature(userId, feature, data);
      response = await getUserPreferencesByUserIdAndFeature(userId, feature, tenantId);
    } else {
      await updateDashboardPreferencesByTenantIdAndFeature(tenantId, feature, data);
      response = await getPreferencesByTenantIdAndFeature(tenantId, feature);
    }
    const record = response?.data ?? response;
    const saved = normalizeNotificationPreferences(record?.preferences);
    if (Object.entries(values).some(([key, enabled]) => saved[key] !== enabled)) {
      throw new Error('The saved notification preference could not be verified');
    }
    setUserPreferences(prev => ({ ...prev, [channel]: saved }));
  };

  return (
    <UserPreferencesContext.Provider 
      value={{ userPreferences, loading, updateUserPreferences }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};
